import React, { useState } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  Campaign,
  Receivable,
  ReceivableItem,
  Condition,
  Gift,
  PseudoItem, 
} from "@dndModels";
import { PALETTE } from "@dndModels/Constants";

import API from "@dndUtilities/system_api";
// import DandDCharacterUtilities from "../../utilities/character_utilities";
import CommonUtilities from "@utilities/common_utilities";
import { makeStyles } from "@utilities/theme_utilities";

import FloatMenu from "@components/input/menus/FloatMenu";
import CampaignItemSelect from "@dndComponents/campaign/CampaignItemSelect";
import SendButton from "@components/input/buttons/icon_buttons/SendButton";
import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import LoadingBox from "@components/input/LoadingBox";
import CampaignConditionSelect from "@dndComponents/campaign/CampaignConditionSelect";
import CampaignGiftSelect from "@dndComponents/campaign/CampaignGiftSelect";
import StringBox from "@components/input/strings/StringBox";

const api = API.getInstance();
// const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

interface Props {
  campaign: Campaign;
}

export default function SendToCharacters({ campaign }: Props) {
  const [receivable_type, setReceivableType] = useState<"Condition" | "Item" | "Gift" | "Item Enhancement" | "Message">("Item");
  const [selected_pseudo_items, setSelectedPseudoItems] = useState<{ item: PseudoItem, count: number }[]>([]);
  const [selected_conditions, setSelectedConditions] = useState<{ item: Condition, count: number }[]>([]);
  const [selected_gifts, setSelectedGifts] = useState<{ item: Gift, count: number }[]>([]);
  const [selected_char_ids, setSelectedCharIds] = useState<string[]>([]);
  const [processing, setProcessing] = useState(false);
  const [sending_message, setSendingMessage] = useState("");
  const [message, setMessage] = useState("");
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main,
        border: "1px solid"
      },
    });
  });
  
  const { classes, cx } = useStyles();

  const renderSendButton = () => {
    if (selected_char_ids.length > 0) {
      if (receivable_type === "Item") {
        if (selected_pseudo_items.length > 0) {
          return (
            <Grid
              size={{
                xs: 12
              }}>
              <SendButton
                disabled={processing}
                onClick={async () => {
                  setProcessing(true);
                  for (const char_id of selected_char_ids) {
                    const char = campaign.characters.find(o => o._id === char_id);
                    if (char) {
                      setSendingMessage(`Sending Items to ${char.name}`);
                      const receivable = new Receivable();
                      receivable.from_id = "DM";
                      receivable.char_id = char._id;
                      receivable.sent_dt = common_util.get_time_number();
                      receivable.message = message;
                      receivable.origin_id = api.temp_id;
                      receivable.type = "Mail";
                      for (const s of selected_pseudo_items) {
                        receivable.items.push(new ReceivableItem({
                          type: "Item",
                          obj_id: s.item._id,
                          count: s.count,
                        }));
                      }
                      await api.upsertObject("receivable", receivable);
                    }
                  }
                  setProcessing(false);
                  setSendingMessage("Sending Complete");
                }}
              />
            </Grid>
          );
        }
      } else if (receivable_type === "Condition") {
        if (selected_conditions.length > 0) {
          return (
            <Grid
              size={{
                xs: 12
              }}>
              <SendButton
                disabled={processing}
                onClick={async () => {
                  setProcessing(true);
                  for (const char_id of selected_char_ids) {
                    const char = campaign.characters.find(o => o._id === char_id);
                    if (char) {
                      for (const s of selected_conditions) {
                        setSendingMessage(`Sending ${s.item.name}${s.count === 1 ? "" : " x " + s.count} to ${char.name}`);
                        const receivable = new Receivable();
                        receivable.from_id = "DM";
                        receivable.char_id = char._id;
                        receivable.sent_dt = common_util.get_time_number();
                        receivable.message = message;
                        receivable.origin_id = api.temp_id;
                        receivable.type = "Mail";
                        receivable.items = [new ReceivableItem({
                          type: "Condition",
                          obj_id: s.item._id,
                          count: s.count,
                        })];
                        await api.upsertObject("receivable", receivable);
                      }
                    }
                  }
                  setProcessing(false);
                  setSendingMessage("Sending Complete");
                }}
              />
            </Grid>
          );
        }
      } else if (receivable_type === "Gift") {
        if (selected_gifts.length > 0) {
          return (
            <Grid
              size={{
                xs: 12
              }}>
              <SendButton
                disabled={processing}
                onClick={async () => {
                  setProcessing(true);
                  for (const char_id of selected_char_ids) {
                    const char = campaign.characters.find(o => o._id === char_id);
                    if (char) {
                      for (const s of selected_gifts) {
                        setSendingMessage(`Sending ${s.item.name}${s.count === 1 ? "" : " x " + s.count} to ${char.name}`);
                        const receivable = new Receivable();
                        receivable.from_id = "DM";
                        receivable.char_id = char._id;
                        receivable.sent_dt = common_util.get_time_number();
                        receivable.message = message;
                        receivable.origin_id = api.temp_id;
                        receivable.type = "Mail";
                        receivable.items = [new ReceivableItem({
                          type: "Gift",
                          obj_id: s.item._id,
                          count: s.count,
                        })];
                        await api.upsertObject("receivable", receivable);
                      }
                    }
                  }
                  setProcessing(false);
                  setSendingMessage("Sending Complete");
                }}
              />
            </Grid>
          );
        }
      }
      
      if (message.length > 0) {
        return (
          <Grid
            size={{
              xs: 12
            }}>
            <SendButton
              disabled={processing}
              onClick={async () => {
                setProcessing(true);
                for (const char_id of selected_char_ids) {
                  const char = campaign.characters.find(o => o._id === char_id);
                  if (char) {
                    setSendingMessage(`Sending Message to ${char.name}`);
                    const receivable = new Receivable();
                    receivable.from_id = "DM";
                    receivable.char_id = char._id;
                    receivable.sent_dt = common_util.get_time_number();
                    receivable.message = message;
                    receivable.items = [];
                    receivable.origin_id = api.temp_id;
                    receivable.type = "Mail";
                    await api.upsertObject("receivable", receivable);
                  }
                }
                setProcessing(false);
                setSendingMessage("Sending Complete");
              }}
            />
          </Grid>
        );
      }
    }
    return null;
  }

  const renderCharactersForSending = () => {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          className="header"
          size={{
            xs: 12
          }}>
          Select Characters
        </Grid>
        { campaign.characters.filter(o => o.active && !o.is_archived).map(char => {
          let className = "";
          let style: React.CSSProperties | undefined = undefined;
          if (selected_char_ids.includes(char._id)) {
            style = {
              backgroundColor: "black",
              color: PALETTE.secondary.light,
              border: "1px solid"
            };
            className = "padded";
          } else {
            className = cx(classes.root, "padded");
          }
          return (
            <Grid
              key={char._id}
              size={{
                xs: 12
              }}>
              <div className="padded">
                <Grid container spacing={0} direction="row" style={style} className={className}>
                  <Grid
                    className="clickable"
                    onClick={() => {
                      if (selected_char_ids.includes(char._id)) {
                        setSelectedCharIds(selected_char_ids.filter(o => o !== char._id));
                      } else {
                        setSelectedCharIds([...selected_char_ids, char._id]);
                      }
                    }}
                    size={{
                      xs: 12
                    }}>
                    { char.name }
                  </Grid>
                </Grid>
              </div>
            </Grid>
          );
        })}
        { receivable_type !== "Message" &&
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Message to Include"
              value={message}
              onBlur={setMessage}
            />
          </Grid>
        }
        { selected_char_ids.length > 0 && 
          <>
            <Grid
              size={{
                xs: 12
              }}>
              <DeleteButton label="Unselect All" onClick={() => { setSelectedCharIds([]); }} />
            </Grid>
          </>
        }
        { renderSendButton() }
        { sending_message.length > 0 &&
          <Grid
            size={{
              xs: 12
            }}>
            { processing ? <LoadingBox message={sending_message} /> : sending_message }
          </Grid>
        }
      </Grid>
    );
  }

  const renderItems = () => {
    return (
      <CampaignItemSelect 
        campaign={campaign} 
        selected={selected_pseudo_items}
        onSelect={setSelectedPseudoItems}
      />
    );
  }

  const renderConditions = () => {
    return (
      <CampaignConditionSelect 
        campaign={campaign} 
        selected={selected_conditions}
        onSelect={setSelectedConditions}
      />
    );
  }

  const renderGifts = () => {
    return (
      <CampaignGiftSelect 
        campaign={campaign} 
        selected={selected_gifts}
        onSelect={setSelectedGifts}
      />
    );
  }

  const renderMessage = () => {
    return (
      <Grid
        size={{
          xs: 12
        }}>
        <StringBox name="Message to Include" multiline value={message} onBlur={setMessage} />
      </Grid>
    );
  }

  const renderWhatWereSending = () => {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <FloatMenu
            options={["Item","Condition","Gift","Item Enhancement","Message"]}
            selected={receivable_type}
            onSelect={(changed: string) => {
              setReceivableType(changed as "Condition" | "Item" | "Gift" | "Item Enhancement" | "Message");
            }}
          />
        </Grid>
        { receivable_type === "Item" ?
          renderItems()
        : receivable_type === "Condition" ?
          renderConditions()
        : receivable_type === "Gift" ?
          renderGifts()
        : receivable_type === "Message" ?
          renderMessage()
        :
          <>
            <Grid
              size={{
                xs: 12
              }}>Not Implemented Yet</Grid>
          </>
        }
      </Grid>
    );
  }
  
  return (
    <Grid container spacing={0} direction="row" 
      style={{ maxHeight: window.innerHeight - 34 }}>
      <Grid
        className="header centered"
        size={{
          xs: 12
        }}>
        Send Things to Characters
      </Grid>
      <Grid
        size={{
          xs: 3
        }}>
        { renderCharactersForSending() }
      </Grid>
      <Grid
        size={{
          xs: 9
        }}>
        { renderWhatWereSending() }
      </Grid>
    </Grid>
  );
}
